import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import "./Home.css";

const Tutorials = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [selectedType, setSelectedType] = useState("all"); // "all", "hotel", "restaurant"

  const videoTutorials = [
    { id: 1, videoId: "PIlPLHBNu9Y", title: "Part 1: How to Login and Navigate the Dashboard", type: "hotel" },
    { id: 2, videoId: "kl0WSvWHPWw", title: "Part 2: Room Booking Dashboard", type: "hotel" },
    { id: 3, videoId: "uyukehGdJ2Q", title: "Part 3: Hotel Management (Room Booking Dashboard)", type: "hotel" },
    { id: 4, videoId: "AkccD9q2KHM", title: "Part 4: Room Management (Room Booking Dashboard)", type: "hotel" },
    { id: 5, videoId: "WSqPdz_eSbM", title: "Part 5: View All Bookings, Add New Booking & Analytics", type: "hotel" },
    { id: 6, videoId: "p6Sl5sLuCf0", title: "Part 6: Hotel Gallery & Photo Management", type: "hotel" },
    { id: 7, videoId: "SF9PcOnm0JQ", title: "Part 7: Restaurant Bookings, Revenue & Analytics", type: "restaurant" },
    { id: 8, videoId: "f5TKeT9HL_c", title: "Part 8: Manage Restaurants & Menus", type: "restaurant" },
    { id: 9, videoId: "49wpBq92kFY", title: "Part 9: Table Management (View, Add, Edit & Configuration)", type: "restaurant" },
    { id: 10, videoId: "PPtDMKkuweQ", title: "Part 10: Advanced Booking Operations", type: "restaurant" },
    { id: 11, videoId: "6VE8AnE6Yhs", title: "Part 11: Reports & Analytics (Daily, Monthly & Revenue Analysis)", type: "restaurant" },
    { id: 12, videoId: "ZXOuxB0YlHw", title: "Part 12: Settings (Restaurant Profile, Business Hours & Menu)", type: "restaurant" },
    { id: 13, videoId: "x6U98_qnK68", title: "Part 13: Analytics (Customer Insights, Peak Hours & Revenue Trends)", type: "restaurant" },
  ];

  const openVideoModal = (video) => {
    setCurrentVideo(video);
    setVideoModalOpen(true);
    // Update URL with video parameters
    navigate(`/tutorials?type=${video.type}&id=${video.id}`, { replace: false });
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    setCurrentVideo(null);
    // Remove video parameters from URL
    navigate('/tutorials', { replace: false });
  };

  const handleShareVideo = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Video link copied to clipboard!');
    });
  };

  // Check URL params on mount and open video if specified
  useEffect(() => {
    const videoType = searchParams.get('type');
    const videoId = searchParams.get('id');
    
    if (videoType && videoId) {
      const video = videoTutorials.find(
        (v) => v.type === videoType && v.id === parseInt(videoId)
      );
      
      if (video) {
        setCurrentVideo(video);
        setVideoModalOpen(true);
      }
    }
  }, [searchParams]);

  return (
    <>
      <SEO 
        title="Video Tutorials - Trivana Hospitality"
        description="Learn how to use Trivana Merchant with our comprehensive step-by-step video tutorials. Master hotel and restaurant management."
        keywords="trivana tutorials, hotel management tutorials, restaurant management guide, video tutorials"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section with Dark Background */}
        <section className="py-16 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-white" style={{fontFamily: 'Montserrat, sans-serif'}}>
                📺 Video Tutorials
              </h1>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8">
                Learn how to use Trivana Merchant with our step-by-step video guides. Watch all 13 tutorials to master the platform.
              </p>
              
              {/* Filter Buttons */}
              <div className="flex flex-wrap justify-center gap-4 mb-8 animate-fade-in-up delay-200">
                <button
                  onClick={() => setSelectedType("all")}
                  className={`px-6 py-3 text-base font-bold rounded-lg transition-all shadow-lg ${
                    selectedType === "all"
                      ? "bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] text-white scale-105"
                      : "bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-300"
                  }`}
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  🎯 All Tutorials
                </button>
                <button
                  onClick={() => setSelectedType("hotel")}
                  className={`px-6 py-3 text-base font-bold rounded-lg transition-all shadow-lg ${
                    selectedType === "hotel"
                      ? "bg-gradient-to-r from-blue-600 to-blue-800 text-white scale-105"
                      : "bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-300"
                  }`}
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  🏨 Hotel Management (Part 1-6)
                </button>
                <button
                  onClick={() => setSelectedType("restaurant")}
                  className={`px-6 py-3 text-base font-bold rounded-lg transition-all shadow-lg ${
                    selectedType === "restaurant"
                      ? "bg-gradient-to-r from-green-600 to-green-800 text-white scale-105"
                      : "bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-300"
                  }`}
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  🍽️ Restaurant Management (Part 7-13)
                </button>
              </div>
              
              <a 
                href="https://www.youtube.com/playlist?list=PLqmNnYv68lpNMGFyGzfiVVqz2JopF0oUG"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-all shadow-lg animate-fade-in-up delay-300"
                style={{fontFamily: 'Montserrat, sans-serif'}}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Watch Full Playlist on YouTube
              </a>
            </div>
          </div>
          {/* Decorative elements */}
          <div className="absolute top-20 left-10 w-40 h-40 bg-[#4299f5]/20 rounded-full blur-3xl animate-blob"></div>
          <div className="absolute bottom-20 right-10 w-60 h-60 bg-[#4299f5]/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        </section>

        {/* Video Cards Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fade-in-up delay-500">
              {videoTutorials
                .filter(video => selectedType === "all" || video.type === selectedType)
                .map((video) => {
                const thumbnailUrl = `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`;
                
                return (
                  <button
                    key={video.id}
                    onClick={() => openVideoModal(video)}
                    className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#4299f5] group w-full text-left"
                  >
                    <div className="relative aspect-video bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] overflow-hidden">
                      <img 
                        src={thumbnailUrl}
                        alt={video.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                          <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                      <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.type === "hotel" ? "🏨 Hotel" : "🍽️ Restaurant"}
                      </div>
                      <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        Part {video.id}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-gray-900 text-sm line-clamp-2 group-hover:text-[#4299f5] transition-colors" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {video.title}
                      </h3>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {videoModalOpen && currentVideo && (
          <div
            onClick={closeVideoModal}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4"
            style={{backdropFilter: 'blur(8px)'}}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl overflow-hidden"
            >
              <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50">
                <h3 className="font-bold text-xl text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {currentVideo.title}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleShareVideo}
                    className="text-blue-600 hover:text-blue-700 transition-colors p-2 hover:bg-blue-50 rounded-lg flex items-center gap-2"
                    title="Share this video"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span className="text-sm font-semibold hidden sm:inline">Share</span>
                  </button>
                  <button
                    onClick={closeVideoModal}
                    className="text-gray-500 hover:text-gray-900 transition-colors p-2 hover:bg-gray-200 rounded-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="relative w-full" style={{paddingBottom: '56.25%', backgroundColor: '#000'}}>
                <iframe
                  key={currentVideo.videoId}
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${currentVideo.videoId}?autoplay=1&rel=0&modestbranding=1`}
                  title={currentVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Tutorials;
